/**
 * Cryptographic hash utilities
 *
 * Provides cross-platform hashing functions that work in both Node.js and browser environments.
 * Uses js-sha256 library for pure JavaScript SHA-256 implementation that works everywhere.
 */

import { sha256 } from 'js-sha256';

/**
 * Generate SHA-256 hash of data
 * @param data - The data to hash
 * @returns The hash as a Uint8Array (32 bytes)
 */
export function sha256Bytes(data: Uint8Array): Uint8Array {
  // Use js-sha256 library which works in both Node.js and browsers
  const hashArray = sha256.array(data);
  return new Uint8Array(hashArray);
}

/**
 * Generate SHA-256 hash of data as hex string
 * @param data - The data to hash
 * @returns The hash as a hex string
 */
export function sha256String(data: Uint8Array): string {
  // Use js-sha256 library which works in both Node.js and browsers
  return sha256.hex(data);
}

/**
 * Generate SHA-256 hash of string data
 * @param text - The string to hash
 * @returns The hash as a hex string
 */
export function sha256Text(text: string): string {
  // Use js-sha256 library which works in both Node.js and browsers
  return sha256.hex(text);
}

/**
 * Generate a shorter hash for use as identifiers (first 16 characters of SHA-256)
 * @param data - The data to hash
 * @returns A short hex string (16 characters)
 */
export function shortHash(data: Uint8Array): string {
  const fullHash = sha256String(data);
  return fullHash.substring(0, 16);
}

/**
 * Generate a shorter hash of string data
 * @param text - The string to hash
 * @returns A short hex string (16 characters)
 */
export function shortHashText(text: string): string {
  const fullHash = sha256Text(text);
  return fullHash.substring(0, 16);
}
